import { supabase } from '../../lib/supabase';

export type StoryReaction = 'like' | 'love' | 'laugh' | 'wow' | 'sad' | 'angry';

export interface Story {
  id: string;
  author_id: string;
  author_username: string | null;
  author_avatar_url: string | null;
  caption: string | null;
  image_path: string;
  created_at: string;
  expires_at: string;
}

export function getPublicStoryUrl(imagePath: string): string {
  const { data } = supabase.storage.from('stories').getPublicUrl(imagePath);
  return data.publicUrl;
}

export async function listActiveStories(): Promise<Story[]> {
  const { data, error } = await supabase
    .from('stories_view')
    .select('id, author_id, author_username, author_avatar_url, caption, image_path, created_at, expires_at')
    .gt('expires_at', new Date().toISOString())
    .order('created_at', { ascending: false })
    .limit(100);

  if (error) throw error;
  return (data || []) as Story[];
}

export async function createStory(params: { authorId: string; file: File; caption?: string | null }): Promise<void> {
  const { authorId, file, caption } = params;

  const extFromName = file.name.includes('.') ? file.name.split('.').pop() : undefined;
  const extFromType = file.type?.split('/')[1];
  const ext = (extFromName || extFromType || 'jpg').toLowerCase();
  const id = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : String(Date.now());

  const path = `${authorId}/${id}.${ext}`;

  const { error: uploadError } = await supabase.storage
    .from('stories')
    .upload(path, file, {
      cacheControl: '3600',
      upsert: false,
      contentType: file.type || undefined,
    });

  if (uploadError) throw uploadError;

  const { error: insertError } = await supabase.from('stories').insert({
    author_id: authorId,
    caption: caption || null,
    image_path: path,
  });

  if (insertError) throw insertError;
}

export async function softDeleteStory(params: { storyId: string; authorId: string }): Promise<void> {
  const { storyId, authorId } = params;
  const { data, error } = await supabase
    .from('stories')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', storyId)
    .eq('author_id', authorId)
    .select('id')
    .maybeSingle();
  if (error) throw error;
  if (!data) throw new Error('You can only delete your own stories.');
}

export async function listReactionsForStories(storyIds: string[]): Promise<Array<{ story_id: string; user_id: string; reaction: StoryReaction }>> {
  if (storyIds.length === 0) return [];

  const { data, error } = await supabase
    .from('story_reactions')
    .select('story_id, user_id, reaction')
    .in('story_id', storyIds);

  if (error) throw error;
  return (data || []) as any;
}

export async function upsertReaction(params: { storyId: string; userId: string; reaction: StoryReaction }): Promise<void> {
  const { storyId, userId, reaction } = params;
  const { error } = await supabase
    .from('story_reactions')
    .upsert(
      {
        story_id: storyId,
        user_id: userId,
        reaction,
      },
      { onConflict: 'story_id,user_id' }
    );
  if (error) throw error;
}

